/**
 * Life statistics calculations
 */

export interface LifeStats {
    // Basic time stats
    weeksLived: number;
    totalWeeks: number;
    weeksRemaining: number;
    percentLived: number;

    // Days
    daysLived: number;
    daysRemaining: number;

    // Reflective stats (estimates)
    heartbeats: number;        // ~100,800 per day
    breaths: number;           // ~17,280 per day
    sunrises: number;          // Days lived
    fullMoons: number;         // ~12.37 per year
    sleepHours: number;        // ~8 hours per day
    mealsEaten: number;        // ~3 per day

    // Age
    ageYears: number;
    ageMonths: number;
    ageDays: number;

    // Current week info
    currentWeekOfYear: number;
    currentYearOfLife: number;
}

/**
 * Calculate age in various units
 */
export function calculateAge(birthDate: Date, now: Date = new Date()): {
    years: number;
    months: number;
    days: number;
    totalDays: number;
} {
    const diffMs = now.getTime() - birthDate.getTime();
    const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    let years = now.getFullYear() - birthDate.getFullYear();
    let months = now.getMonth() - birthDate.getMonth();
    let days = now.getDate() - birthDate.getDate();

    if (days < 0) {
        months--;
        const prevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        days += prevMonth.getDate();
    }

    if (months < 0) {
        years--;
        months += 12;
    }

    return { years, months, days, totalDays };
}

/**
 * Calculate weeks lived since birth
 */
export function calculateWeeksLived(birthDate: Date, now: Date = new Date()): number {
    const diffMs = now.getTime() - birthDate.getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24 * 7));
}

/**
 * Calculate total weeks in expected lifespan
 */
export function calculateTotalWeeks(lifeExpectancyYears: number): number {
    return Math.floor(lifeExpectancyYears * 52);
}

/**
 * Get the current week of the year (1-52)
 */
export function getCurrentWeekOfYear(date: Date = new Date()): number {
    const start = new Date(date.getFullYear(), 0, 1);
    const diff = date.getTime() - start.getTime();
    const oneWeek = 1000 * 60 * 60 * 24 * 7;
    return Math.ceil(diff / oneWeek);
}

/**
 * Calculate all life statistics
 */
export function calculateLifeStats(birthDate: Date, lifeExpectancyYears: number): LifeStats {
    const now = new Date();
    const age = calculateAge(birthDate, now);

    const weeksLived = calculateWeeksLived(birthDate, now);
    const totalWeeks = calculateTotalWeeks(lifeExpectancyYears);
    const weeksRemaining = Math.max(0, totalWeeks - weeksLived);

    const daysLived = age.totalDays;
    const totalDays = Math.floor(lifeExpectancyYears * 365.25);
    const daysRemaining = Math.max(0, totalDays - daysLived);

    const percentLived = Math.min(100, (weeksLived / totalWeeks) * 100);

    // Reflective stats (using average estimates)
    const heartbeats = daysLived * 100800;          // ~100,800 per day (70 bpm)
    const breaths = daysLived * 17280;              // ~17,280 per day (12 per min)
    const sunrises = daysLived;                      // One per day
    const fullMoons = Math.floor(age.years * 12.37); // ~12.37 full moons per year
    const sleepHours = daysLived * 8;                // ~8 hours per day
    const mealsEaten = daysLived * 3;                // ~3 per day

    const currentWeekOfYear = getCurrentWeekOfYear(now);
    const currentYearOfLife = age.years + 1;

    return {
        weeksLived,
        totalWeeks,
        weeksRemaining,
        percentLived,
        daysLived,
        daysRemaining,
        heartbeats,
        breaths,
        sunrises,
        fullMoons,
        sleepHours,
        mealsEaten,
        ageYears: age.years,
        ageMonths: age.months,
        ageDays: age.days,
        currentWeekOfYear,
        currentYearOfLife,
    };
}

/**
 * Format large numbers with commas
 */
export function formatNumber(num: number): string {
    return num.toLocaleString('en-US');
}

/**
 * Format percentage
 */
export function formatPercent(num: number, decimals: number = 1): string {
    return num.toFixed(decimals) + '%';
}

/**
 * Format a large number in a human-readable way
 */
export function formatCompactNumber(num: number): string {
    if (num >= 1_000_000_000) {
        return (num / 1_000_000_000).toFixed(1) + 'B';
    }
    if (num >= 1_000_000) {
        return (num / 1_000_000).toFixed(1) + 'M';
    }
    if (num >= 1_000) {
        return (num / 1_000).toFixed(1) + 'K';
    }
    return num.toString();
}

// ============================================
// Phase 2: Societal Context Stats
// ============================================

export interface SocietalStats {
    peopleEncountered: number;      // Estimated people crossed paths with
    birthsDuringLife: number;       // ~132M/year × years
    deathsDuringLife: number;       // ~62M/year × years
}

// Constants for societal calculations
const PEOPLE_ENCOUNTERED_LIFETIME = 80000;  // Research estimate for typical lifetime
const BIRTHS_PER_YEAR = 132_000_000;        // ~132M births/year (2024 data)
const DEATHS_PER_YEAR = 62_000_000;         // ~62M deaths/year (2024 data)

/**
 * Calculate societal context statistics
 */
export function calculateSocietalStats(ageYears: number, lifeExpectancyYears: number): SocietalStats {
    // Scale people encountered based on age vs expected lifespan
    const lifeProgress = Math.min(1, ageYears / lifeExpectancyYears);
    const peopleEncountered = Math.floor(PEOPLE_ENCOUNTERED_LIFETIME * lifeProgress);

    // Births and deaths during lifetime
    const birthsDuringLife = Math.floor(BIRTHS_PER_YEAR * ageYears);
    const deathsDuringLife = Math.floor(DEATHS_PER_YEAR * ageYears);

    return {
        peopleEncountered,
        birthsDuringLife,
        deathsDuringLife,
    };
}

// ============================================
// Phase 2: Cosmic & Natural Stats
// ============================================

export interface CosmicStats {
    distanceThroughSpace: number;   // km traveled with Earth through space
    sunshineHoursExperienced: number; // Based on country average
}

// Constants for cosmic calculations
const EARTH_ORBITAL_DISTANCE_PER_YEAR = 940_000_000; // ~940 million km/year

/**
 * Calculate cosmic and natural perspective statistics
 */
export function calculateCosmicStats(ageYears: number, sunshineHoursPerYear: number): CosmicStats {
    // Distance traveled through space (Earth's orbit around the Sun)
    const distanceThroughSpace = Math.floor(EARTH_ORBITAL_DISTANCE_PER_YEAR * ageYears);

    // Total sunshine hours experienced
    const sunshineHoursExperienced = Math.floor(sunshineHoursPerYear * ageYears);

    return {
        distanceThroughSpace,
        sunshineHoursExperienced,
    };
}
